/**
 * Tool performance — how many issues the audit got resolved, and on which cars.
 *
 * NAMED AND PLACED DELIBERATELY. It sits under `/inventory/audit/performance`,
 * reached from a button on the audit page, and calls itself "Tool Performance"
 * rather than "Performance". As a top-level Inventory nav entry it read as
 * *inventory* performance — turn, aging, sales (Ian, 2026-08-13: "the
 * performance page itself looks like actual inventory performace enhancement").
 * This page is about how well INVI is working, not how well the inventory is.
 *
 * Ian, 2026-08-13, after rejecting a first version as too complicated: "i just
 * want it to show how many issues got resolved. what vehicles got fixed. the
 * problems that those vehicles had."
 *
 * So the page is a count and a list, and deliberately nothing else. The first
 * version led with three outcome tiles, a median time-to-fix, and a note about
 * folded duplicate observations — every one of those numbers is real and still
 * stored, but together they read as a methodology section in front of the
 * answer.
 *
 * WHAT COUNTS, and why the number is small. Ian, seeing 1,681 cleared problems:
 * "but those arent issues i resolved." He was right — 831 were the website feed
 * catching up and 536 were duplicate-VIN records flickering between vendor
 * syncs. So a car reaches this list only if its problem was FIXED (a car that
 * sold took its problem with it) and it carries a WORKED MARK, the one piece of
 * evidence that a person touched it. Same two conditions as the backend's
 * `exporters/performance_xlsx.py::resolved_by_people`, against the same fields.
 *
 * Nothing infers human involvement from how long a problem sat. "It was open 23
 * hours so someone probably worked it" is a guess, and INVI does not guess.
 *
 * The filtering is silent — no outcome labels, no "N cleared automatically"
 * beside the headline. The one place the excluded work is named is the empty
 * state, so a report showing nothing can say why instead of looking broken.
 */
import React, { useCallback, useEffect, useState } from "react"
import { Link } from "react-router-dom"

import Layout from "../../components/Layout"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import ErrorBoundary from "../../components/ErrorBoundary"
import { useDealerGroup } from "../../context/DealerGroupContext"
import {
  PerformanceReport,
  exportPerformanceWorkbook,
  getPerformanceReport,
} from "../../api/performance"
import { THEME } from "../../config/theme"

/** Matches the backend's DEFAULT_WINDOW_DAYS so the first paint agrees with it. */
const DEFAULT_WINDOW_DAYS = 14

/** Local (Pacific-for-Ian) calendar date as yyyy-mm-dd, never a UTC shift. */
function isoDay(date: Date): string {
  const year = date.getFullYear()
  const month = `${date.getMonth() + 1}`.padStart(2, "0")
  const day = `${date.getDate()}`.padStart(2, "0")
  return `${year}-${month}-${day}`
}

function daysAgo(count: number): string {
  const date = new Date()
  date.setDate(date.getDate() - count)
  return isoDay(date)
}

export default function PerformanceReportPage() {
  return (
    <Layout>
      <PerformanceReportContent />
    </Layout>
  )
}

function PerformanceReportContent() {
  const { dealerGroupId } = useDealerGroup()
  const [start, setStart] = useState(() => daysAgo(DEFAULT_WINDOW_DAYS - 1))
  const [end, setEnd] = useState(() => isoDay(new Date()))
  const [report, setReport] = useState<PerformanceReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [downloading, setDownloading] = useState(false)

  const load = useCallback(() => {
    if (dealerGroupId === null) return undefined
    let cancelled = false
    setReport(null)
    setError(null)
    getPerformanceReport(start, end, dealerGroupId)
      .then((next) => {
        if (!cancelled) setReport(next)
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load the report")
        }
      })
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, start, end])

  useEffect(() => load(), [load])

  const handleDownload = async () => {
    if (dealerGroupId === null) return
    setDownloading(true)
    try {
      const blob = await exportPerformanceWorkbook(start, end, dealerGroupId)
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `INVI Tool Performance ${start} to ${end}.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      // Beside the button, not thrown: a failed download must not take the
      // report down with it.
      setError(err instanceof Error ? err.message : "Download failed")
    } finally {
      setDownloading(false)
    }
  }

  if (dealerGroupId === null) {
    return (
      <div style={{ padding: THEME.spacing.lg }}>
        <PageHeading />
        <DealerGroupRequiredNotice surface="tool performance" />
      </div>
    )
  }

  return (
    <div style={{ padding: THEME.spacing.lg }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-end",
          flexWrap: "wrap",
          gap: THEME.spacing.md,
          marginBottom: THEME.spacing.md,
        }}
      >
        <PageHeading />
        <div style={{ display: "flex", alignItems: "flex-end", gap: THEME.spacing.sm }}>
          <DateField label="From" value={start} onChange={setStart} />
          <DateField label="To" value={end} onChange={setEnd} />
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading || report === null}
            style={downloadButtonStyle(downloading || report === null)}
          >
            {downloading ? "Preparing…" : "Download Excel"}
          </button>
        </div>
      </div>

      {error !== null && (
        <div style={noticeStyle(THEME.colors.semantic.danger)} role="alert">
          {error}
        </div>
      )}

      {report === null && error === null && (
        <div style={mutedStyle}>Loading the report…</div>
      )}

      {report !== null && (
        <ErrorBoundary surface="performance-report">
          <ReportBody report={report} />
        </ErrorBoundary>
      )}
    </div>
  )
}

function PageHeading() {
  return (
    <div>
      <Link
        to="/inventory/audit"
        style={{
          fontSize: 12.5,
          color: THEME.colors.neutral.light.textSecondary,
          textDecoration: "none",
        }}
      >
        ← Inventory Audit
      </Link>
      <h1
        style={{
          fontSize: 24,
          fontWeight: 500,
          letterSpacing: "-0.5px",
          margin: `${THEME.spacing.xs} 0 0 0`,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      >
        Tool Performance
      </h1>
    </div>
  )
}

function ReportBody({ report }: { report: PerformanceReport }) {
  // Fixed AND claimed by someone — see the file docstring for both conditions.
  const resolved = report.vehicles.filter(
    (vehicle) => vehicle.outcome === "fixed" && vehicle.worked_by !== null
  )

  if (resolved.length === 0) {
    return (
      <div style={{ ...mutedStyle, lineHeight: 1.6 }}>
        Nothing marked worked was resolved between {report.start} and{" "}
        {report.end}.
        {report.cleared_automatically > 0 && (
          <>
            {" "}
            {report.cleared_automatically.toLocaleString()} problems did clear on
            their own in that window — vendor feeds catching up with each other,
            which nobody worked. Tick <strong>worked</strong> on a car in the
            worklist and it appears here once the problem clears.
          </>
        )}
      </div>
    )
  }

  return (
    <>
      <div style={{ marginBottom: THEME.spacing.lg }}>
        <div style={{ fontSize: 44, fontWeight: 500, color: THEME.colors.navy }}>
          {resolved.length.toLocaleString()}
        </div>
        <div style={{ fontSize: 14, color: THEME.colors.neutral.light.textPrimary }}>
          {resolved.length === 1 ? "issue resolved" : "issues resolved"}
        </div>
      </div>

      <div
        style={{
          border: `1px solid ${THEME.colors.neutral.light.border}`,
          borderRadius: THEME.borderRadius.md,
          background: THEME.colors.neutral.light.surface,
          overflow: "hidden",
        }}
      >
        <div style={{ maxHeight: 640, overflowY: "auto", overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <Th>Vehicle</Th>
                <Th>Stock</Th>
                <Th>VIN</Th>
                <Th>Problem</Th>
                <Th>Flagged</Th>
                <Th>Resolved</Th>
                <Th>Notes</Th>
              </tr>
            </thead>
            <tbody>
              {resolved.map((vehicle) => (
                <tr key={`${vehicle.vin}-${vehicle.problem_type}-${vehicle.resolved_at}`}>
                  <Td>
                    {[vehicle.year, vehicle.make, vehicle.model]
                      .filter(Boolean)
                      .join(" ") || "—"}
                  </Td>
                  <Td>{vehicle.stock ?? "—"}</Td>
                  <Td>{vehicle.vin}</Td>
                  <Td>{vehicle.label}</Td>
                  <Td>{vehicle.flagged_at ? vehicle.flagged_at.slice(0, 10) : "—"}</Td>
                  <Td>{vehicle.resolved_at.slice(0, 10)}</Td>
                  {/* The one column that wraps: a note is a sentence, and
                      truncating it would hide the thing it is here to say. */}
                  <Td wrap>
                    {vehicle.notes.length === 0 ? (
                      <span style={{ color: THEME.colors.neutral.light.textSecondary }}>—</span>
                    ) : (
                      vehicle.notes.map((note, index) => (
                        <div key={index} style={{ marginTop: index === 0 ? 0 : 4 }}>
                          {note}
                        </div>
                      ))
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

function DateField({
  label, value, onChange,
}: {
  label: string
  value: string
  onChange: (next: string) => void
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: THEME.spacing.xs }}>
      <span style={mutedStyle}>{label}</span>
      <input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        style={{
          padding: "6px 8px",
          fontSize: 13,
          fontFamily: THEME.fonts.primary,
          border: `1px solid ${THEME.colors.neutral.light.border}`,
          borderRadius: THEME.borderRadius.sm,
          background: THEME.colors.white,
          color: THEME.colors.neutral.light.textPrimary,
        }}
      />
    </label>
  )
}

function Th({
  children, align = "left",
}: {
  children: React.ReactNode
  align?: "left" | "right"
}) {
  return (
    <th
      style={{
        textAlign: align,
        padding: "10px 14px",
        fontSize: 11.5,
        fontWeight: 600,
        letterSpacing: "0.03em",
        textTransform: "uppercase",
        color: THEME.colors.white,
        background: THEME.colors.navy,
        position: "sticky",
        top: 0,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  )
}

function Td({
  children, align = "left", wrap = false,
}: {
  children: React.ReactNode
  align?: "left" | "right"
  /** Lets the cell run to several lines. Only the notes column wants this. */
  wrap?: boolean
}) {
  return (
    <td
      style={{
        textAlign: align,
        padding: "8px 14px",
        fontSize: 12.5,
        borderTop: `1px solid ${THEME.colors.neutral.light.border}`,
        color: THEME.colors.neutral.light.textPrimary,
        whiteSpace: wrap ? "normal" : "nowrap",
        ...(wrap ? { minWidth: 260, maxWidth: 460 } : {}),
      }}
    >
      {children}
    </td>
  )
}

const mutedStyle: React.CSSProperties = {
  fontSize: 12.5,
  color: THEME.colors.neutral.light.textSecondary,
}

const tableStyle: React.CSSProperties = {
  width: "100%",
  borderCollapse: "collapse",
  fontFamily: THEME.fonts.primary,
}

function noticeStyle(color: string): React.CSSProperties {
  return {
    padding: THEME.spacing.md,
    marginBottom: THEME.spacing.md,
    border: `1px solid ${color}`,
    borderRadius: THEME.borderRadius.md,
    background: THEME.colors.white,
    fontSize: 12.5,
    color,
  }
}

function downloadButtonStyle(disabled: boolean): React.CSSProperties {
  return {
    padding: "7px 14px",
    fontSize: 12.5,
    fontFamily: THEME.fonts.primary,
    borderRadius: THEME.borderRadius.sm,
    border: `1px solid ${THEME.colors.navy}`,
    background: disabled ? THEME.colors.silver : THEME.colors.navy,
    color: THEME.colors.white,
    cursor: disabled ? "not-allowed" : "pointer",
  }
}
